import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import { cancelPurchaseOrder, createPurchaseOrder, getPurchaseOrderComparison, getPurchaseOrderLookup, getPurchaseOrders, updatePurchaseOrder } from "../../api/purchaseOrderApi";
import { getApiErrorMessage } from "../../utils/concurrency";
import { formatDateUk } from "../../utils/dateDisplay";
import PurchaseOrderAccountSelect from "./PurchaseOrderAccountSelect";
import PurchaseOrderLookup from "./PurchaseOrderLookup";

const money = (value) => value == null ? "—" : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);
const emptyForm = () => ({ supplier: null, account: null, amount: "", description: "", from_date: "", to_date: "" });
const emptyPage = { results: [], total: 0 };

export default function PurchaseOrdersPanel({ propertyId, onMessage }) {
  const location = useLocation();
  const navigate = useNavigate();
  const yearParam = new URLSearchParams(location.search).get("po_year");
  const [year, setYear] = useState(null);
  const [ready, setReady] = useState(false);
  const [orders, setOrders] = useState(emptyPage);
  const [comparison, setComparison] = useState(emptyPage);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [comparisonPage, setComparisonPage] = useState(0);
  const [comparisonSize, setComparisonSize] = useState(10);
  const [status, setStatus] = useState("");
  const [supplier, setSupplier] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setOrders(emptyPage);
    setComparison(emptyPage);
    getPurchaseOrderLookup(propertyId, "financial-years", { selected_id: yearParam }).then((res) => {
      if (cancelled) return;
      if (yearParam && !res.data.selected) { setError("Financial year not found on this property."); return; }
      setYear(yearParam ? res.data.selected : res.data.default);
      setPage(0);
      setComparisonPage(0);
      setReady(true);
    }).catch((e) => { if (!cancelled) setError(getApiErrorMessage(e, "Failed to load financial years.")); });
    return () => { cancelled = true; };
  }, [propertyId, yearParam]);

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    const params = { fiscal_year_id: year?.id };
    Promise.all([
      getPurchaseOrders(propertyId, { ...params, page: page + 1, page_size: size, status, supplier_id: supplier?.id, account_id: account?.id }),
      getPurchaseOrderComparison(propertyId, { ...params, page: comparisonPage + 1, page_size: comparisonSize }),
    ]).then(([orderRes, comparisonRes]) => {
      if (!cancelled) {
        setOrders(orderRes.data);
        setComparison(comparisonRes.data);
        if (orderRes.data.page) setPage(orderRes.data.page - 1);
        if (comparisonRes.data.page) setComparisonPage(comparisonRes.data.page - 1);
      }
    }).catch((e) => { if (!cancelled) setError(getApiErrorMessage(e, "Failed to load purchase orders.")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [propertyId, ready, year?.id, page, size, comparisonPage, comparisonSize, status, supplier?.id, account?.id, refresh]);

  const changeYear = (value) => {
    const params = new URLSearchParams(location.search);
    if (value) params.set("po_year", value.id); else params.delete("po_year");
    navigate({ pathname: location.pathname, search: params.toString() });
  };
  const openEdit = (order) => {
    setFormError("");
    setForm(order ? { ...order, supplier: { id: order.supplier_id, label: order.supplier_name }, account: { id: order.account_id, label: `${order.account_code} — ${order.account_name}` } } : emptyForm());
  };
  const editField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const save = async (event) => {
    event.preventDefault();
    if (!form.supplier || !form.account) { setFormError("Select a supplier and CoA account."); return; }
    setSaving(true);
    setFormError("");
    try {
      const payload = { supplier_id: form.supplier.id, account_id: form.account.id, amount: form.amount,
        description: form.description, from_date: form.from_date, to_date: form.to_date,
        ...(form.id ? { row_version: form.row_version } : {}) };
      const res = form.id ? await updatePurchaseOrder(propertyId, form.id, payload) : await createPurchaseOrder(propertyId, payload);
      setForm(null);
      setWarnings(res.data.warnings || []);
      setRefresh((value) => value + 1);
      onMessage?.(`${res.data.po_number} saved.`);
    } catch (e) {
      if (e.response?.status === 409) {
        setRefresh((value) => value + 1);
        setFormError("This PO changed elsewhere. Close this form and reopen it to load the latest version.");
      } else setFormError(getApiErrorMessage(e, "Failed to save purchase order."));
    }
    finally { setSaving(false); }
  };
  const cancel = async () => {
    setSaving(true);
    setError("");
    try {
      await cancelPurchaseOrder(propertyId, cancelTarget.id, cancelTarget.row_version);
      onMessage?.(`${cancelTarget.po_number} cancelled.`);
      setCancelTarget(null);
      setWarnings([]);
      setRefresh((value) => value + 1);
    } catch (e) { setError(getApiErrorMessage(e, "Failed to cancel purchase order.")); setCancelTarget(null); }
    finally { setSaving(false); }
  };
  const filterChange = useCallback((setter, value) => { setter(value); setPage(0); }, []);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Typography variant="h5">Purchase Orders</Typography>
        <PurchaseOrderLookup propertyId={propertyId} kind="financial-years" label="Financial year" value={year} onChange={changeYear} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openEdit(null)}>Raise purchase order</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {warnings.map((warning) => <Alert key={warning} severity="warning" sx={{ mb: 1 }}>{warning}</Alert>)}
      <Typography variant="h6" sx={{ mt: 3 }}>Purchase-order register</Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", my: 2 }}>
        <TextField select size="small" label="Status" value={status} sx={{ minWidth: 160 }} onChange={(e) => filterChange(setStatus, e.target.value)}>
          <MenuItem value="">All statuses</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
        <PurchaseOrderLookup propertyId={propertyId} kind="suppliers" label="Supplier filter" value={supplier} onChange={(value) => filterChange(setSupplier, value)} />
        <PurchaseOrderLookup propertyId={propertyId} kind="accounts" label="CoA filter" value={account} onChange={(value) => filterChange(setAccount, value)} />
      </Box>
      <TableContainer>
        <Table size="small" aria-label="Purchase order register">
          <TableHead><TableRow>{["PO number", "Supplier", "Description", "CoA", "Amount GBP", "From", "To", "Status", "Actions"].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>{orders.results.map((order) => <TableRow key={order.id}>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{order.po_number}</TableCell><TableCell>{order.supplier_name}</TableCell><TableCell sx={{ whiteSpace: "pre-wrap", minWidth: 180 }}>{order.description}</TableCell><TableCell>{order.account_code} — {order.account_name}</TableCell><TableCell>{money(order.amount)}</TableCell><TableCell>{formatDateUk(order.from_date)}</TableCell><TableCell>{formatDateUk(order.to_date)}</TableCell><TableCell><Chip size="small" label={order.status} /></TableCell>
            <TableCell>{order.status === "active" && <Box sx={{ display: "flex", gap: 1 }}><Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => openEdit(order)}>Edit</Button><Button variant="outlined" color="error" size="small" startIcon={<CancelIcon />} onClick={() => setCancelTarget(order)}>Cancel PO</Button></Box>}</TableCell>
          </TableRow>)}{!loading && orders.results.length === 0 && <TableRow><TableCell colSpan={9}>No purchase orders match these filters.</TableCell></TableRow>}</TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={orders.total} page={page} rowsPerPage={size} rowsPerPageOptions={[10, 25, 50]} onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} />

      <Typography variant="h6">Budget and PO commitments by CoA</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>Commitments are purchase orders, not paid expenditure. Amounts are gross and apportioned by the days covered in each financial year.</Typography>
      {ready && !comparison.budget && <Alert severity="info" sx={{ mb: 1 }}>No agreed budget{year ? " for this financial year" : "; no current financial year is configured"}.</Alert>}
      {comparison.budget && <Typography variant="body2">{comparison.budget.name} ({comparison.budget.status})</Typography>}
      {loading && <CircularProgress size={24} aria-label="Loading purchase orders" />}
      <TableContainer>
        <Table size="small" aria-label="Purchase order budget comparison">
          <TableHead><TableRow><TableCell>CoA</TableCell><TableCell>Account</TableCell><TableCell align="right">Budget GBP</TableCell><TableCell align="right">Committed GBP</TableCell><TableCell align="right">Remaining GBP</TableCell></TableRow></TableHead>
          <TableBody>{comparison.results.map((row) => <TableRow key={row.account_id} sx={row.over_budget ? { bgcolor: "warning.light" } : {}}>
            <TableCell>{row.code}</TableCell><TableCell>{row.name}</TableCell><TableCell align="right">{money(row.budget)}</TableCell><TableCell align="right">{money(row.committed)}</TableCell><TableCell align="right">{money(row.remaining)}{row.over_budget && " — over budget"}</TableCell>
          </TableRow>)}{!loading && comparison.results.length === 0 && <TableRow><TableCell colSpan={5}>No accounts available.</TableCell></TableRow>}</TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={comparison.total} page={comparisonPage} rowsPerPage={comparisonSize} rowsPerPageOptions={[10, 25, 50]} onPageChange={(_, value) => setComparisonPage(value)} onRowsPerPageChange={(e) => { setComparisonSize(Number(e.target.value)); setComparisonPage(0); }} />

      <Dialog open={Boolean(form)} onClose={() => { if (!saving) setForm(null); }} fullWidth maxWidth="sm">
        {form && <Box component="form" onSubmit={save}>
          <DialogTitle>{form.id ? `Edit ${form.po_number}` : "Raise purchase order"}</DialogTitle>
          <DialogContent><Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <PurchaseOrderLookup propertyId={propertyId} kind="suppliers" label="Supplier" value={form.supplier} onChange={(value) => editField("supplier", value)} disabled={saving} required />
            <PurchaseOrderAccountSelect propertyId={propertyId} label="CoA account" value={form.account} onChange={(value) => editField("account", value)} disabled={saving} required />
            <TextField required label="Gross amount GBP" type="number" inputProps={{ min: "0.01", step: "0.01", max: "999999999999.99" }} value={form.amount} onChange={(e) => editField("amount", e.target.value)} disabled={saving} />
            <TextField required label="Description" multiline minRows={3} inputProps={{ maxLength: 10000 }} value={form.description} onChange={(e) => editField("description", e.target.value)} disabled={saving} />
            <TextField required label="From date" type="date" InputLabelProps={{ shrink: true }} value={form.from_date} onChange={(e) => editField("from_date", e.target.value)} disabled={saving} />
            <TextField required label="To date" type="date" InputLabelProps={{ shrink: true }} inputProps={{ min: form.from_date }} value={form.to_date} onChange={(e) => editField("to_date", e.target.value)} disabled={saving} />
            <Typography variant="caption">PO numbers are assigned automatically. Any budget overspend will be highlighted after saving.</Typography>
          </Box></DialogContent>
          <DialogActions><Button variant="outlined" startIcon={<CloseIcon />} onClick={() => setForm(null)} disabled={saving}>Close</Button><Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} disabled={saving}>{saving ? "Saving…" : "Save purchase order"}</Button></DialogActions>
        </Box>}
      </Dialog>
      <Dialog open={Boolean(cancelTarget)} onClose={() => { if (!saving) setCancelTarget(null); }}>
        <DialogTitle>Cancel {cancelTarget?.po_number}?</DialogTitle>
        <DialogContent><DialogContentText>The purchase order will remain in the register and audit history, but will no longer count towards commitments.</DialogContentText></DialogContent>
        <DialogActions><Button variant="outlined" startIcon={<CloseIcon />} onClick={() => setCancelTarget(null)} disabled={saving}>Keep PO</Button><Button variant="outlined" color="error" startIcon={saving ? <CircularProgress size={16} /> : <CancelIcon />} onClick={cancel} disabled={saving}>Confirm cancellation</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
